import Occasion from '@support/Occasion';
import { Set } from 'immutable';
import { ID, IDRecord, SearchSort, SearchSortBy } from '@app/types';
import Contact from '@models/Contact';

const lessThanNumber = (a: string, b: string) => a < b;
const lessThanString = (a: string, b: string) => (a || '').localeCompare(b || '') < 0;
const lessThanDate = (a: Occasion, b: Occasion) => a.isBefore(b);

const getKeyValue = (contact: Contact, key: SearchSortBy) => {
  if (key === 'closeness') {
    return contact?.score;
  }

  if (key === 'lastName') {
    return contact?.sortLastName;
  }

  if (key === 'firstName') {
    return contact?.sortFirstName;
  }

  return contact?.[key] || undefined;
};

export const comparatorForSort = (sort: SearchSort, getContact: (id: ID) => Contact) => {
  const dir = sort.direction === 'asc' ? 1 : -1;

  let lessThan: (a: number | string | Occasion, b: number | string | Occasion) => boolean =
    lessThanString;

  if (['firstInteraction', 'lastInteraction', 'created'].includes(sort.name)) {
    lessThan = lessThanDate;
  } else if (sort.name === 'closeness') {
    lessThan = lessThanNumber;
  }

  return (a: IDRecord, b: IDRecord) => {
    const aValue = getKeyValue(getContact(String(a.id)), sort.name);
    const bValue = getKeyValue(getContact(String(b.id)), sort.name);

    if (aValue === bValue || (!aValue && !bValue)) {
      return 0;
    }

    if (!aValue && bValue) {
      return dir;
    }

    if (!bValue && aValue) {
      return -dir;
    }

    const isLessThan = lessThan(aValue, bValue);

    if (isLessThan) {
      return -dir;
    }

    if (!isLessThan) {
      return dir;
    }

    return 0;
  };
};

export const mapAndSortContacts = (
  contactIDs: Set<ID>,
  getContact: (id: ID) => Contact,
  sort: SearchSort,
): Contact[] => {
  const comparator = comparatorForSort(sort, getContact);
  return contactIDs
    .toArray()
    .map((id) => ({ id }))
    .sort(comparator)
    .map(({ id }) => getContact(id));
};
